
import React from 'react';
import { DataConnector, LinkedConnector } from '@/types';
import { StandardizedConnection } from '@/types/connections';
import FileImportView from './FileImportView';
import InitialOptionsView from './InitialOptionsView';
import DatabaseConnectorView from './DatabaseConnectorView';

interface ConnectorModalContentProps {
  view: 'initial' | 'file' | 'database' | 'loading';
  loading: boolean;
  error: string | null;
  connectors: DataConnector[];
  selectedConnector: DataConnector | null;
  isConnecting: boolean;
  linkedConnectors: StandardizedConnection[];
  onFileOption: () => void;
  onDatabaseOption: () => void;
  onBack: () => void;
  onRemoveConnector: (id: string, connectionId: string) => Promise<void>;
  onConnectorSelect: (connector: DataConnector) => void;
  onConnect: () => Promise<void>;
  onConnectionSuccess: (connector: StandardizedConnection) => Promise<void>;
  onClose: () => void;
  onLoadConnectors: () => Promise<void>;
  isLoadingConnections?: boolean;
  isLoadingAfterRedirect?: boolean;
  shopName?: string;
  onShopNameChange?: (value: string) => void;
  onPlaidReauth?: (connector: StandardizedConnection) => void;
  onBeginConnectionsRefresh?: () => void;
}

const ConnectorModalContent: React.FC<ConnectorModalContentProps> = ({
  view,
  loading,
  error,
  connectors,
  selectedConnector,
  isConnecting,
  linkedConnectors,
  onFileOption,
  onDatabaseOption,
  onBack,
  onRemoveConnector,
  onConnectorSelect,
  onConnect,
  onConnectionSuccess,
  onClose,
  onLoadConnectors,
  isLoadingConnections = false,
  isLoadingAfterRedirect = false,
  shopName = '',
  onShopNameChange,
  onPlaidReauth,
  onBeginConnectionsRefresh
}) => {
  if (loading || isLoadingConnections || isLoadingAfterRedirect) {
    const loadingMessage = isLoadingAfterRedirect 
      ? 'Loading your new connection...' 
      : isLoadingConnections 
        ? 'Loading your connections...' 
        : 'Loading connectors...';

    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4" role='progressbar'></div>
        <p className="text-gray-600">{loadingMessage}</p>
      </div>
    );
  }

  const handleConnectionSuccess = async (connector: StandardizedConnection) => {
    await onConnectionSuccess(connector);
  };

  switch (view) {
    case 'initial':
      return (
        <InitialOptionsView
          onFileOption={onFileOption}
          onDatabaseOption={onDatabaseOption}
          linkedConnectors={linkedConnectors}
          onRemoveConnector={onRemoveConnector}
          onClose={onClose}
          onPlaidReauth={onPlaidReauth}
        />
      );
    case 'file':
      return (
        <FileImportView 
          onBack={onBack}
          onSuccess={handleConnectionSuccess}
          isConnecting={isConnecting}
          onImportStart={() => {}}
        />
      );
    case 'database':
      return (
        <DatabaseConnectorView 
          connectors={connectors}
          loading={loading}
          selectedConnector={selectedConnector}
          onConnectorSelect={onConnectorSelect}
          onConnect={onConnect}
          isConnecting={isConnecting}
          error={error}
          onBack={onBack}
          onLoadConnectors={onLoadConnectors}
          onSuccess={handleConnectionSuccess}
          shopName={shopName}
          onShopNameChange={onShopNameChange}
          onBeginConnectionsRefresh={onBeginConnectionsRefresh}
        />
      );
    default:
      return null;
  }
};

export default ConnectorModalContent;
